/**
 * The viewport stats readout's rows, like Unreal's `stat unit`: frame rate,
 * frame and CPU time, what the last frame drew, and the resolution it drew at
 * (and the JS heap, where the browser reports it). Pure, so the formatting
 * is tested without a renderer.
 *
 * The viewport renders on demand, so an idle viewport has no frame rate; it
 * says so rather than showing 0.
 */
import type { FrameTimingSnapshot } from "../../core/frameTimings";
import type { RenderApi, RenderStats } from "../../render/RenderBackend";
import type { EditorLayout } from "../shell/layout";

export interface ViewportStatsInput {
  /** The frame loop is producing frames right now. */
  readonly running: boolean;
  readonly timings: FrameTimingSnapshot;
  readonly render: RenderStats;
  readonly api: RenderApi;
  /** The viewport's size in CSS pixels (drawn at the render stats' pixel ratio). */
  readonly size: { readonly width: number; readonly height: number };
  /** The page's JS heap in use, where the browser reports it (Chrome's `performance.memory`). */
  readonly heapBytes: number | null;
  /** Why an idle viewport is still, when that's a setting's doing (see ambientEffects). */
  readonly idleReason?: string | null;
}

export type StatsLine = readonly [label: string, value: string];

export function statsLines(input: ViewportStatsInput): StatsLine[] {
  const { running, timings, render, api, size, heapBytes } = input;
  const drawn = (css: number) => Math.round(css * render.pixelRatio);

  return [
    ["FPS", running ? timings.fps.toFixed(1) : "idle"],
    ["Frame", `${timings.frameMs.toFixed(1)} ms`],
    ["CPU", `${timings.cpuMs.toFixed(1)} ms`],
    ["Draws", String(render.drawCalls)],
    ["Tris", compactCount(render.triangles)],
    ["Geo / Tex", `${render.geometries} / ${render.textures}`],
    ["RHI", api],
    // What's actually drawn: the fill-rate cost that matters most on phones.
    ["Res", `${drawn(size.width)} × ${drawn(size.height)} @${render.pixelRatio}x`],
    ...(heapBytes === null ? [] : [["Heap", `${Math.round(heapBytes / 1024 / 1024)} MB`] as const]),
  ];
}

/** The Stats details setting: follow the layout, or the visitor's own choice. */
export type StatsDetails = "auto" | "open" | "folded";

/**
 * Whether the readout shows its full table. Automatically, it does on desktop,
 * where there's room and it shows the engine at work, and folds to one line
 * on phones, where the scene needs the space.
 */
export function statsOpen(details: StatsDetails, layout: EditorLayout): boolean {
  if (details === "auto") return layout === "desktop";
  return details === "open";
}

/** The readout collapsed to one line, like Unreal's `stat fps`: frame rate and frame time. */
export function statsSummary({ running, timings, idleReason }: ViewportStatsInput): string {
  const idle = idleReason ? `idle (${idleReason})` : "idle";
  const rate = running ? `${timings.fps.toFixed(1)} FPS` : idle;
  return `${rate} · ${timings.frameMs.toFixed(1)} ms`;
}

/** 812 → "812", 96 140 → "96.1k", 2 450 000 → "2.45M". */
function compactCount(value: number): string {
  if (value >= 1_000_000) return `${Number((value / 1_000_000).toFixed(2))}M`;
  if (value >= 1000) return `${Number((value / 1000).toFixed(1))}k`;
  return String(value);
}
